import { createRouter, createWebHistory } from 'vue-router';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// --- View Imports ---
const Home = () => import('../views/Home.vue');
const Dashboard = () => import('../views/Dashboard.vue');
const Quiz = () => import('../views/Quiz.vue');
const Leaderboard = () => import('../views/Leaderboard.vue');
const Admin = () => import('../views/Admin.vue');
const Users = () => import('../views/Users.vue');
const ManageQuestions = () => import('../views/ManageQuestions.vue');
const Rules = () => import('../views/Rules.vue');
const NotFound = () => import('../views/NotFound.vue');

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    // UPDATED: This guard now correctly waits for the auth state to be confirmed.
    beforeEnter: async (to, from, next) => {
      const user = await getCurrentUser();
      if (user) {
        // If user is logged in, redirect them to the dashboard.
        next({ name: 'Dashboard' });
      } else {
        // If no user, show the login page.
        next();
      }
    },
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: '/quiz/:id',
    name: 'Quiz',
    component: Quiz,
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/quiz/:id/leaderboard',
    name: 'Leaderboard',
    component: Leaderboard,
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin',
    name: 'Admin',
    component: Admin,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: Users,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/quiz/:id/questions',
    name: 'ManageQuestions',
    component: ManageQuestions,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/rules',
    name: 'Rules',
    component: Rules,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

/**
 * Helper function to wait for Firebase Auth to be initialized.
 * This prevents race conditions where the app tries to check the user's
 * status before Firebase has had a chance to load it.
 */
const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    const removeListener = onAuthStateChanged(
      getAuth(),
      (user) => {
        removeListener();
        resolve(user);
      },
      reject
    );
  });
};

// --- Global Navigation Guard ---
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  if (requiresAuth) {
    const user = await getCurrentUser();
    if (user) {
      next();
    } else {
      next({ 
        name: 'Home', 
        query: { redirect: to.fullPath } 
      });
    }
  } else {
    // For public routes, we don't need to do anything special here.
    // The individual beforeEnter guard on the 'Home' route will handle redirection
    // if the user is already logged in.
    next();
  }
});

export default router;
